import { useCallback, useState } from 'react'
import { lumi } from '@/lib/lumi'
import type { JobCategory } from '@/data/jobs'
import { emailCandidate_DossierPending } from '@/lib/emails'

/**
 * Clés des documents conservés dans le dossier professionnel auto-entrepreneur.
 * Aligné avec JobDetail.tsx (DocKey).
 */
export type ProDossierDocKey =
  | 'cartePro'
  | 'assurancePro'
  | 'diplome'
  | 'caepmns'
  | 'pse'
  | 'recyclagePse'
  | 'recyclageFcBnssa'
  | 'kbis'
  | 'certificatInsee'

export interface ProDossier {
  _id: string
  category: JobCategory
  diplomeType?: string
  files: Partial<Record<ProDossierDocKey, string>>
  fullName?: string
  email?: string
  phone?: string
  // Informations entreprise (auto-entrepreneur)
  siret?: string
  raisonSociale?: string
  adresseEntreprise?: string
  telephoneEntreprise?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface ProDossierInput {
  category: JobCategory
  diplomeType?: string
  files: Partial<Record<ProDossierDocKey, string>>
  fullName?: string
  email?: string
  phone?: string
  // Informations entreprise (auto-entrepreneur)
  siret?: string
  raisonSociale?: string
  adresseEntreprise?: string
  telephoneEntreprise?: string
}

/**
 * Fonction pure (sans état React) pour récupérer un dossier AE par creator.
 * Utilisée par les appels batch (Backoffice) pour éviter toute boucle de re-render.
 */
export async function fetchProDossierByCreator(
  creator: string,
  email?: string
): Promise<ProDossier | null> {
  if (!creator) return null
  try {
    const { list } = await lumi.entities.pro_dossiers.list({
      filter: { creator },
      sort: { updatedAt: -1 },
      limit: 1,
    })
    const items = list as ProDossier[]
    if (items.length > 0) return items[0]
    if (email) {
      const byEmail = await lumi.entities.pro_dossiers.list({
        filter: { email: email.toLowerCase() },
        sort: { updatedAt: -1 },
        limit: 1,
      })
      const items2 = byEmail.list as ProDossier[]
      if (items2.length > 0) return items2[0]
    }
    return null
  } catch (e) {
    console.error('pro_dossiers.list failed:', e)
    return null
  }
}

/**
 * Hook de gestion du dossier professionnel auto-entrepreneur en base Lumi.
 * Le dossier est unique par utilisateur (creator), avec upsert "récupérer le plus récent puis update / sinon create".
 */
export function useProDossier() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Récupère le dossier le plus récent appartenant à l'utilisateur (par creator).
   * Si non trouvé et `email` fourni, tente une recherche secondaire par email
   * (utile lorsqu'un candidat invité crée un compte plus tard).
   */
  const fetchMine = useCallback(
    async (creator: string, email?: string): Promise<ProDossier | null> => {
      if (!creator) return null
      setLoading(true)
      setError(null)
      try {
        const result = await fetchProDossierByCreator(creator, email)
        return result
      } catch (e) {
        console.error('pro_dossiers fetchMine failed:', e)
        setError('Impossible de charger votre dossier professionnel.')
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Crée un nouveau dossier (à utiliser si fetchMine renvoie null).
   */
  const createDossier = useCallback(
    async (data: ProDossierInput, creator: string): Promise<ProDossier> => {
      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        category: data.category,
        files: data.files,
        creator,
        // Validation admin : tout nouveau dossier auto-entrepreneur est en attente par défaut
        validationStatus: 'pending',
        createdAt: now,
        updatedAt: now,
      }
      if (data.diplomeType) payload.diplomeType = data.diplomeType
      if (data.fullName) payload.fullName = data.fullName
      if (data.email) payload.email = data.email.toLowerCase()
      if (data.phone) payload.phone = data.phone
      if (data.siret) payload.siret = data.siret
      if (data.raisonSociale) payload.raisonSociale = data.raisonSociale
      if (data.adresseEntreprise) payload.adresseEntreprise = data.adresseEntreprise
      if (data.telephoneEntreprise) payload.telephoneEntreprise = data.telephoneEntreprise

      const created = await lumi.entities.pro_dossiers.create(payload)
      console.log('pro_dossiers.create OK', created)

      // Email 8 — Dossier AE en attente de validation (best-effort)
      if (data.email) {
        void emailCandidate_DossierPending({
          candidateEmail: data.email,
          candidateName: data.fullName,
          category: data.category,
        })
      }

      return created as ProDossier
    },
    []
  )

  /**
   * Met à jour un dossier existant (par _id string).
   */
  const updateDossier = useCallback(
    async (id: string, patch: Partial<ProDossierInput>): Promise<ProDossier> => {
      if (typeof id !== 'string' || !id) {
        throw new Error('Invalid pro_dossier id')
      }
      const now = new Date().toISOString()
      const updatePayload: Record<string, unknown> = {
        updatedAt: now,
        // Toute mise à jour candidat repasse le dossier en attente de validation
        validationStatus: 'pending',
      }
      if (patch.category) updatePayload.category = patch.category
      if (patch.diplomeType !== undefined) updatePayload.diplomeType = patch.diplomeType
      if (patch.files) updatePayload.files = patch.files
      if (patch.fullName !== undefined) updatePayload.fullName = patch.fullName
      if (patch.email !== undefined) updatePayload.email = patch.email?.toLowerCase()
      if (patch.phone !== undefined) updatePayload.phone = patch.phone
      if (patch.siret !== undefined) updatePayload.siret = patch.siret
      if (patch.raisonSociale !== undefined) updatePayload.raisonSociale = patch.raisonSociale
      if (patch.adresseEntreprise !== undefined) updatePayload.adresseEntreprise = patch.adresseEntreprise
      if (patch.telephoneEntreprise !== undefined) updatePayload.telephoneEntreprise = patch.telephoneEntreprise

      const updated = await lumi.entities.pro_dossiers.update(id, updatePayload)
      console.log('pro_dossiers.update OK', id)

      // Email 8 — Dossier repassé en attente après mise à jour candidat (best-effort)
      if (patch.email && patch.category) {
        void emailCandidate_DossierPending({
          candidateEmail: patch.email,
          candidateName: patch.fullName,
          category: patch.category,
        })
      }

      return updated as ProDossier
    },
    []
  )

  /**
   * Upsert : crée si aucun dossier existant pour ce creator, sinon met à jour le plus récent.
   * Retourne le dossier final.
   */
  const upsertDossier = useCallback(
    async (data: ProDossierInput, creator: string): Promise<ProDossier> => {
      const existing = await fetchMine(creator, data.email)
      if (existing && existing._id) {
        return updateDossier(existing._id, data)
      }
      return createDossier(data, creator)
    },
    [fetchMine, createDossier, updateDossier]
  )

  return {
    loading,
    error,
    fetchMine,
    createDossier,
    updateDossier,
    upsertDossier,
  }
}
